import React, { useState, useEffect, useMemo } from 'react';
import {
  Award, Plus, Search, Edit2, Trash2, Phone, Check,
  TrendingUp, Users, Heart, LayoutGrid, Table as TableIcon,
  Filter, Sparkles, CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { calegApi } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';

const DAPIL_LIST = [
  'Dapil Sukabumi 1',
  'Dapil Sukabumi 2',
  'Dapil Sukabumi 3',
  'Dapil Sukabumi 4',
  'Dapil Sukabumi 5',
  'Dapil Sukabumi 6',
];

const DAPIL_META_FALLBACK = {
  'Dapil Sukabumi 1': { suara_pks_2024: 15719, target_suara_2029: 21419, target_kursi: 'Bertahan 1 kursi', badge_color: 'gray' },
  'Dapil Sukabumi 2': { suara_pks_2024: 23392, target_suara_2029: 33492, target_kursi: 'Bertahan 1 kursi', badge_color: 'gray' },
  'Dapil Sukabumi 3': { suara_pks_2024: 29914, target_suara_2029: 58764, target_kursi: 'Target 1 → 2 kursi', badge_color: 'orange' },
  'Dapil Sukabumi 4': { suara_pks_2024: 31200, target_suara_2029: 55000, target_kursi: 'Target 2 kursi', badge_color: 'orange' },
  'Dapil Sukabumi 5': { suara_pks_2024: 18500, target_suara_2029: 28000, target_kursi: 'Bertahan 1 kursi', badge_color: 'gray' },
  'Dapil Sukabumi 6': { suara_pks_2024: 16800, target_suara_2029: 24000, target_kursi: 'Bertahan 1 kursi', badge_color: 'gray' },
};

const TIER_STYLE = {
  Kuat: 'text-emerald-600 font-bold',
  Sedang: 'text-amber-600 font-bold',
  Lemah: 'text-gray-500 font-medium',
};

const STATUS_BADGE = {
  Penjaringan: 'bg-blue-50 text-blue-700 border-blue-200',
  DCS: 'bg-amber-50 text-amber-700 border-amber-200',
  DCT: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Terpilih: 'bg-purple-50 text-purple-700 border-purple-200',
};

const normalizeWa = (hp) => {
  if (!hp) return '';
  let s = String(hp).replace(/\D/g, '');
  if (s.startsWith('0')) s = '62' + s.slice(1);
  else if (!s.startsWith('62')) s = '62' + s;
  return s;
};

const CalegLineup = () => {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'admin_pusat', 'admin_input'].includes(user?.role);

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'

  // Filters
  const [selectedDapil, setSelectedDapil] = useState('all');
  const [selectedTier, setSelectedTier] = useState('all');
  const [search, setSearch] = useState('');

  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    nama: '',
    nomor_urut: '',
    dapil: 'Dapil Sukabumi 1',
    tingkat: 'DPRD Kabupaten',
    suara_2024: '',
    elek: '',
    tier: 'Sedang',
    is_incumbent: false,
    jenis_kelamin: 'L',
    hp: '',
    status: 'Penjaringan',
    target_suara: '',
    basis_wilayah: '',
    keterangan: '',
  });

  // Delete State
  const [deleteId, setDeleteId] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        calegApi.list({
          dapil: selectedDapil !== 'all' ? selectedDapil : undefined,
          search: search || undefined,
        }),
        calegApi.summary(),
      ]);
      setItems(listRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      toast({ title: 'Gagal memuat data caleg', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDapil]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadData();
  };

  const handleOpenAdd = (defaultDapil) => {
    setEditingItem(null);
    setForm({
      nama: '',
      nomor_urut: '',
      dapil: defaultDapil || (selectedDapil !== 'all' ? selectedDapil : 'Dapil Sukabumi 1'),
      tingkat: 'DPRD Kabupaten',
      suara_2024: '',
      elek: '',
      tier: 'Sedang',
      is_incumbent: false,
      jenis_kelamin: 'L',
      hp: '',
      status: 'Penjaringan',
      target_suara: '',
      basis_wilayah: '',
      keterangan: '',
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setForm({
      nama: item.nama || '',
      nomor_urut: item.nomor_urut ?? '',
      dapil: item.dapil || 'Dapil Sukabumi 1',
      tingkat: item.tingkat || 'DPRD Kabupaten',
      suara_2024: item.suara_2024 ?? '',
      elek: item.elek || '',
      tier: item.tier || 'Sedang',
      is_incumbent: !!item.is_incumbent,
      jenis_kelamin: item.jenis_kelamin || 'L',
      hp: item.hp || '',
      status: item.status || 'Penjaringan',
      target_suara: item.target_suara ?? '',
      basis_wilayah: item.basis_wilayah || '',
      keterangan: item.keterangan || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nama.trim()) {
      toast({ title: 'Nama caleg wajib diisi', variant: 'destructive' });
      return;
    }
    const payload = {
      ...form,
      nomor_urut: form.nomor_urut ? parseInt(form.nomor_urut, 10) : null,
      suara_2024: form.suara_2024 ? parseInt(form.suara_2024, 10) : 0,
      target_suara: form.target_suara ? parseInt(form.target_suara, 10) : 0,
    };

    try {
      if (editingItem) {
        await calegApi.update(editingItem.id, payload);
        toast({ title: 'Data Caleg berhasil diperbarui' });
      } else {
        await calegApi.create(payload);
        toast({ title: 'Caleg berhasil ditambahkan' });
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ title: 'Gagal menyimpan data caleg', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await calegApi.remove(deleteId);
      toast({ title: 'Data caleg berhasil dihapus' });
      setDeleteId(null);
      loadData();
    } catch (err) {
      toast({ title: 'Gagal menghapus data', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  // Group items by Dapil for cards view
  const dapilMap = useMemo(() => {
    const map = {};
    DAPIL_LIST.forEach((d) => {
      map[d] = [];
    });
    items.forEach((c) => {
      const d = c.dapil || 'Dapil Sukabumi 1';
      if (!map[d]) map[d] = [];
      map[d].push(c);
    });
    // Sort each dapil's candidates by nomor_urut or suara_2024 desc
    Object.keys(map).forEach((d) => {
      map[d].sort((a, b) => (a.nomor_urut || 99) - (b.nomor_urut || 99));
    });
    return map;
  }, [items]);

  const displayedDapils = selectedDapil === 'all'
    ? DAPIL_LIST
    : [selectedDapil];

  return (
    <div className="space-y-6">
      {/* PRESENTATION HEADER BANNER */}
      <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-xs relative overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-8 h-1.5 bg-orange-500 rounded-full inline-block"></span>
            <span className="text-xs font-black tracking-widest text-orange-600 uppercase">
              LINE-UP CALEG · DAPIL 1–6
            </span>
          </div>
          <span className="text-[11px] font-bold text-gray-400 tracking-wider uppercase">
            DPD KABUPATEN SUKABUMI
          </span>
        </div>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">
              Line up Caleg 2029
            </h1>
            <p className="text-xs text-gray-500 font-medium mt-1">
              <span className="font-bold text-emerald-600">✓</span> = terpilih 2024 &nbsp;·&nbsp;
              <span className="font-bold">Elek.</span> = % suara terhadap total suara sah dapil &nbsp;·&nbsp;
              Tier berdasarkan elektabilitas.
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 p-1 rounded-xl">
              <button
                onClick={() => setViewMode('cards')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  viewMode === 'cards' ? 'bg-white text-orange-600 shadow-xs' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                Mode Kartu Dapil
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  viewMode === 'table' ? 'bg-white text-orange-600 shadow-xs' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                <TableIcon className="w-3.5 h-3.5" />
                Mode Tabel
              </button>
            </div>

            {isAdmin && (
              <Button
                onClick={() => handleOpenAdd()}
                className="h-9 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-xs gap-1.5 shadow-sm shadow-orange-500/20"
              >
                <Plus className="w-4 h-4" />
                Tambah Caleg
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* FILTER DAPIL TABS */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
        <button
          onClick={() => setSelectedDapil('all')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex-shrink-0 ${
            selectedDapil === 'all'
              ? 'bg-orange-500 text-white shadow-sm shadow-orange-500/20'
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          Semua Dapil
        </button>
        {DAPIL_LIST.map((d) => (
          <button
            key={d}
            onClick={() => setSelectedDapil(d)}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex-shrink-0 ${
              selectedDapil === d
                ? 'bg-orange-500 text-white shadow-sm shadow-orange-500/20'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            {d.replace('Dapil Sukabumi ', 'Dapil ')}
          </button>
        ))}
      </div>

      {/* VIEW MODE 1: CARDS PER DAPIL (EXACT SLIDE PRESENTATION DESIGN) */}
      {viewMode === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {displayedDapils.map((dapilName) => {
            const calegList = dapilMap[dapilName] || [];
            const meta = (summary?.dapil_metadata && summary.dapil_metadata[dapilName]) || DAPIL_META_FALLBACK[dapilName] || {
              suara_pks_2024: 0, target_suara_2029: 0, target_kursi: 'Bertahan 1 kursi', badge_color: 'gray'
            };
            const dapilShort = dapilName.replace('Dapil Sukabumi ', 'Dapil ');

            return (
              <div
                key={dapilName}
                className="bg-white rounded-2xl border border-gray-200 shadow-xs flex flex-col overflow-hidden hover:border-orange-200 transition-colors"
              >
                {/* CARD HEADER */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <h2 className="text-xl font-black text-gray-900 tracking-tight">
                      {dapilShort}
                    </h2>
                    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${
                      meta.badge_color === 'orange'
                        ? 'bg-orange-500 text-white font-extrabold shadow-xs shadow-orange-500/20'
                        : 'bg-gray-600 text-white font-medium'
                    }`}>
                      {meta.target_kursi}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 font-medium">
                    Suara PKS {meta.suara_pks_2024 ? meta.suara_pks_2024.toLocaleString('id-ID') : '-'}
                    &nbsp;·&nbsp;
                    Target suara {meta.target_suara_2029 ? meta.target_suara_2029.toLocaleString('id-ID') : '-'}
                  </p>
                </div>

                {/* CARD TABLE */}
                <div className="flex-1 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-800 text-white font-extrabold text-[11px]">
                        <th className="py-2 px-2.5 text-center w-8">#</th>
                        <th className="py-2 px-2 text-left">Nama Caleg</th>
                        <th className="py-2 px-2 text-right">Suara</th>
                        <th className="py-2 px-2 text-right">Elek.</th>
                        <th className="py-2 px-2.5 text-center">Tier</th>
                        {isAdmin && <th className="py-2 px-1 text-center w-8"></th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {calegList.length === 0 ? (
                        <tr>
                          <td colSpan={isAdmin ? 6 : 5} className="py-8 text-center text-gray-400">
                            Belum ada caleg di {dapilShort}
                          </td>
                        </tr>
                      ) : (
                        calegList.map((row, idx) => {
                          return (
                            <tr key={row.id || idx} className="hover:bg-gray-50/70 transition-colors">
                              <td className="py-2.5 px-2.5 text-center font-bold text-gray-500">
                                {row.nomor_urut || idx + 1}
                              </td>
                              <td className="py-2.5 px-2 font-semibold text-gray-900">
                                <div className="flex items-center gap-1">
                                  <span>{row.nama}</span>
                                  {row.is_incumbent && (
                                    <span className="text-emerald-600 font-black text-sm" title="Terpilih 2024">
                                      ✓
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="py-2.5 px-2 text-right font-bold text-gray-800">
                                {row.suara_2024 ? row.suara_2024.toLocaleString('id-ID') : '-'}
                              </td>
                              <td className="py-2.5 px-2 text-right text-gray-600 font-medium">
                                {row.elek || '-'}
                              </td>
                              <td className="py-2.5 px-2.5 text-center">
                                <span className={TIER_STYLE[row.tier] || 'text-gray-500 font-medium'}>
                                  {row.tier || '-'}
                                </span>
                              </td>
                              {isAdmin && (
                                <td className="py-2.5 px-1 text-center">
                                  <div className="flex items-center justify-center gap-0.5">
                                    <button
                                      onClick={() => handleOpenEdit(row)}
                                      className="p-1 rounded text-gray-400 hover:text-gray-700"
                                      title="Edit"
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => setDeleteId(row.id)}
                                      className="p-1 rounded text-gray-300 hover:text-red-600"
                                      title="Hapus"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {isAdmin && (
                  <div className="p-2.5 border-t border-gray-100 bg-gray-50/50">
                    <button
                      onClick={() => handleOpenAdd(dapilName)}
                      className="w-full py-1 text-xs font-bold text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors flex items-center justify-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Tambah ke {dapilShort}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* VIEW MODE 2: TABLE VIEW */
        <div className="bg-white rounded-2xl border border-gray-200 shadow-xs overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-900 text-white font-extrabold">
              <TableRow>
                <TableHead className="w-12 text-center text-white text-xs font-extrabold">#</TableHead>
                <TableHead className="text-white text-xs font-extrabold">Nama Caleg</TableHead>
                <TableHead className="text-white text-xs font-extrabold">Dapil</TableHead>
                <TableHead className="text-right text-white text-xs font-extrabold">Suara 2024</TableHead>
                <TableHead className="text-right text-white text-xs font-extrabold">Elektabilitas</TableHead>
                <TableHead className="text-center text-white text-xs font-extrabold">Tier</TableHead>
                <TableHead className="text-right text-white text-xs font-extrabold">Target Suara 2029</TableHead>
                <TableHead className="text-white text-xs font-extrabold">Basis Wilayah</TableHead>
                <TableHead className="text-center text-white text-xs font-extrabold">Status</TableHead>
                {isAdmin && <TableHead className="w-20 text-center text-white text-xs font-extrabold">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row, idx) => (
                <TableRow key={row.id || idx} className="hover:bg-gray-50/70">
                  <TableCell className="text-center font-bold text-gray-500 text-xs">
                    {row.nomor_urut || idx + 1}
                  </TableCell>
                  <TableCell className="font-bold text-gray-900 text-xs">
                    <div className="flex items-center gap-1">
                      <span>{row.nama}</span>
                      {row.is_incumbent && (
                        <span className="text-emerald-600 font-black text-sm" title="Terpilih 2024">
                          ✓
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs font-semibold text-gray-700">
                    {row.dapil?.replace('Dapil Sukabumi ', 'Dapil ')}
                  </TableCell>
                  <TableCell className="text-right font-bold text-gray-900 text-xs">
                    {row.suara_2024 ? row.suara_2024.toLocaleString('id-ID') : '-'}
                  </TableCell>
                  <TableCell className="text-right text-xs font-medium text-gray-600">
                    {row.elek || '-'}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={TIER_STYLE[row.tier] || 'text-gray-500 font-medium text-xs'}>
                      {row.tier || '-'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-extrabold text-orange-600 text-xs">
                    {row.target_suara ? row.target_suara.toLocaleString('id-ID') : '-'}
                  </TableCell>
                  <TableCell className="text-xs text-gray-600">
                    {row.basis_wilayah || '-'}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_BADGE[row.status] || 'bg-gray-50 text-gray-600'}`}>
                      {row.status || 'Penjaringan'}
                    </span>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleOpenEdit(row)} className="p-1 rounded hover:bg-gray-100 text-gray-500">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setDeleteId(row.id)} className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* BOTTOM CALLOUT BANNER (EXACT COPY FROM PRESENTATION SLIDE) */}
      <div className="bg-[#1f2937] text-white p-5 rounded-2xl shadow-sm border border-gray-800 flex items-start gap-3">
        <span className="w-1.5 h-10 bg-orange-500 rounded-full flex-shrink-0 mt-0.5"></span>
        <div className="space-y-1">
          <p className="text-sm font-extrabold text-white">
            Fokus 2029: memastikan kesiapan incumbent dan penjajakan 5 besar caleg 2024 di tiap dapil.
          </p>
          <p className="text-xs text-gray-300 font-medium">
            Modal suara terbesar: Solihin 9.896 (D5), Uden 9.553 (D4), Ramzi 9.032 (D4), Amran Lutphi 7.068 (D4), Iwan Ridwan 6.943 (D3).
          </p>
        </div>
      </div>

      {/* FORM MODAL */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-black text-gray-900">
              {editingItem ? 'Edit Caleg' : 'Tambah Caleg Lineup'}
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Isi data calon legislatif dan capaian suara untuk pemetaan 2029.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-bold text-gray-700">Nama Lengkap & Gelar *</Label>
              <Input
                value={form.nama}
                onChange={(e) => setForm({ ...form, nama: e.target.value })}
                placeholder="Contoh: Hj. Leni Liawati, S.Si."
                className="mt-1 rounded-xl h-10 font-medium"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold text-gray-700">Dapil *</Label>
                <Select
                  value={form.dapil}
                  onValueChange={(val) => setForm({ ...form, dapil: val })}
                >
                  <SelectTrigger className="mt-1 rounded-xl h-10 font-medium">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAPIL_LIST.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-bold text-gray-700">Nomor Urut</Label>
                <Input
                  type="number"
                  min="1"
                  max="50"
                  value={form.nomor_urut}
                  onChange={(e) => setForm({ ...form, nomor_urut: e.target.value })}
                  placeholder="1, 2, 3..."
                  className="mt-1 rounded-xl h-10 font-medium"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-bold text-gray-700">Suara 2024</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.suara_2024}
                  onChange={(e) => setForm({ ...form, suara_2024: e.target.value })}
                  placeholder="5700"
                  className="mt-1 rounded-xl h-10 font-medium"
                />
              </div>

              <div>
                <Label className="text-xs font-bold text-gray-700">Elektabilitas (%)</Label>
                <Input
                  value={form.elek}
                  onChange={(e) => setForm({ ...form, elek: e.target.value })}
                  placeholder="2,89%"
                  className="mt-1 rounded-xl h-10 font-medium"
                />
              </div>

              <div>
                <Label className="text-xs font-bold text-gray-700">Tier Kekuatan</Label>
                <Select
                  value={form.tier}
                  onValueChange={(val) => setForm({ ...form, tier: val })}
                >
                  <SelectTrigger className="mt-1 rounded-xl h-10 font-medium">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Kuat">Kuat (Hijau)</SelectItem>
                    <SelectItem value="Sedang">Sedang (Kuning)</SelectItem>
                    <SelectItem value="Lemah">Lemah (Abu)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold text-gray-700">Target Suara 2029</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.target_suara}
                  onChange={(e) => setForm({ ...form, target_suara: e.target.value })}
                  placeholder="11400"
                  className="mt-1 rounded-xl h-10 font-medium"
                />
              </div>

              <div>
                <Label className="text-xs font-bold text-gray-700">Basis Wilayah</Label>
                <Input
                  value={form.basis_wilayah}
                  onChange={(e) => setForm({ ...form, basis_wilayah: e.target.value })}
                  placeholder="Palabuhanratu, Cisolok"
                  className="mt-1 rounded-xl h-10 font-medium"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-100">
              <Checkbox
                id="is_incumbent"
                checked={form.is_incumbent}
                onCheckedChange={(c) => setForm({ ...form, is_incumbent: !!c })}
              />
              <label htmlFor="is_incumbent" className="text-xs font-bold text-gray-800 cursor-pointer">
                Incumbent Terpilih 2024 (Ditandai checklist ✓ di tabel)
              </label>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">
                Batal
              </Button>
              <Button type="submit" className="rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold">
                {editingItem ? 'Simpan Perubahan' : 'Tambah Caleg'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CONFIRM DELETE DIALOG */}
      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(op) => !op && setDeleteId(null)}
        title="Hapus Data Caleg"
        description="Apakah Anda yakin ingin menghapus data calon legislatif ini dari daftar?"
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default CalegLineup;
